import { useCallback } from 'react';

import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { usePinterestClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { SendMediaEventProps } from './types';
import { PinterestClientPayload } from '@/pages/api/tracking/pinterest/pinterestAPIUtils';
import { safeEncodeUrl } from 'presentation/events/utils/ga4Utils';
import { emptyToNull } from 'presentation/events/utils/stringUtils';
import { triggerPinterestCAPIEvent } from 'presentation/events/services/pinterestService';
import { usePathname } from 'next/navigation';
import { PRODUCT_URL_PATH } from 'presentation/events/hooks/useTrackSearchSubmit/constants';

export const useTrackPageViewPinterest = () => {
	const { settings } = useSettings();
	const { userData } = settings;
	const { epik } = usePinterestClickIdentifiers();
	const storeLocation = useTrackingStoreLocation();
	const pathname = usePathname();
	const sendPinterestPageViewEvent = useCallback(
		({ eventId, personInfo, storeId }: SendMediaEventProps) => {
			if (userData?.ENABLE_PINTEREST_CALL !== 'true') return;
			if (pathname?.startsWith(PRODUCT_URL_PATH)) return;
			const pinterestPayload: PinterestClientPayload = {
				event_name: 'page_visit',
				event_id: eventId,
				storeId,
				event_source_url: safeEncodeUrl(window.location.href),
				epik,
				test: userData?.ENABLE_PINTEREST_TEST === 'true',
				custom_data: {
					externalId: getExternalId(),
					email: emptyToNull(personInfo?.email),
					firstName: emptyToNull(personInfo?.firstName),
					lastName: emptyToNull(personInfo?.lastName ?? null),
					phone: emptyToNull(personInfo?.phone ?? null),
					city: emptyToNull(storeLocation?.city ?? null),
					state: emptyToNull(storeLocation?.state ?? null),
					zip: emptyToNull(storeLocation?.zipCode ?? null),
				},
			};

			triggerPinterestCAPIEvent(pinterestPayload);
		},
		[
			epik,
			pathname,
			storeLocation?.city,
			storeLocation?.state,
			storeLocation?.zipCode,
			userData?.ENABLE_PINTEREST_CALL,
			userData?.ENABLE_PINTEREST_TEST,
		]
	);

	return { sendPinterestPageViewEvent };
};
